import React from 'react';

/**
 * VehicleTable Component
 * Displays vehicles in a table format with edit and delete actions
 */
export default function VehicleTable({ vehicles, colors = [], onEdit, onDelete, t, translate }) {
  // Helper function to get hex color code from color name
  const getColorHexCode = (colorName) => {
    if (!colorName) return null;
    // Find color in colors array by name (case-insensitive)
    const colorData = colors.find(c =>
      c.color_name?.toLowerCase() === colorName.toLowerCase()
    );
    if (colorData?.hex_code) {
      return colorData.hex_code;
    }
    // Fallback: return the color name itself (works for English color names like "blue", "red")
    return colorName.toLowerCase();
  };

  const vehicleTypes = [
    { key: 'small_car', label: 'ລົດເກັ່ງນ້ອຍຂະຫນາດນ້ອຍ' },
    { key: 'compact_car_truck', label: 'ລົດເກັ່ງນ້ອຍ (<7 ບ່ອນນັ່ງ), ລົດບັນທຸກຂະຫນາດນ້ອຍ' },
    { key: 'small_bus', label: 'ລົດໂດຍສານຂະຫນາດນ້ອຍ (7-12 ບ່ອນນັ່ງ)' },
    { key: 'medium_bus', label: 'ລົດໂດຍສານຂະຫນາດກາງ (12-24 ບ່ອນນັ່ງ)' },
    { key: 'large_bus', label: 'ລົດໂດຍສານຂະຫນາດໃຫຍ່ (>24 ບ່ອນນັ່ງ)' },
    { key: 'truck_6_wheels', label: 'ລົດບັນທຸກ 6 ລໍ້' },
    { key: 'truck_10_wheels', label: 'ລົດບັນທຸກ 10 ລໍ້' },
    { key: 'truck_10_plus_wheels', label: 'ລົດບັນທຸກ 10 ລໍ້ ຂຶ້ນໄປ' }
  ];

  const getVehicleTypeLabel = (key) => {
    return vehicleTypes.find(vt => vt.key === key)?.label || key;
  };

  return (
    <div className="overflow-x-auto shadow-md mb-6">
      <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 text-sm">
        <thead>
          <tr className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-left font-semibold">
              🚗 {t('vehicles.table.license_plate')}
            </th>
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-left font-semibold">
              🏷️ Plate Type
            </th>
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-left font-semibold">
              👤 User
            </th>
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-left font-semibold">
              ⭐ Customer Type
            </th>
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-left font-semibold">
              🚙 {t('vehicles.table.type')}
            </th>
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-left font-semibold">
              🚚 Truck Type
            </th>
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-left font-semibold">
              🏷️ Make/Model/Year
            </th>
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-left font-semibold">
              🎨 Color
            </th>
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-left font-semibold">
              👤 {t('vehicles.table.owner')}
            </th>
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-center font-semibold">
              💳 Device
            </th>
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-center font-semibold">
              🚦 Status
            </th>
            <th className="border border-gray-300 dark:border-gray-600 p-3 text-center font-semibold">
              🔧 Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {vehicles.length === 0 ? (
            <tr>
              <td colSpan="12" className="text-center p-8 text-gray-500 dark:text-gray-400">
                <div className="text-xl">🔍</div>
                <div className="mt-2">{t('vehicles.table.no_vehicles_found')}</div>
              </td>
            </tr>
          ) : (
            vehicles.map(vehicle => (
              <tr key={vehicle.id} className="hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors">
                {/* License Plate */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  <div className="font-bold text-lg text-blue-700 dark:text-blue-300">
                    {vehicle.license_plate}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    ID: {vehicle.id}
                  </div>
                </td>

                {/* Plate Type */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  {vehicle.plate_type ? (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                      {vehicle.plate_type === 'Private' && '🚗 Private'}
                      {vehicle.plate_type === 'Commercial' && '🚚 Commercial'}
                      {vehicle.plate_type === 'Government' && '🏛️ Government'}
                      {vehicle.plate_type === 'Diplomatic' && '🌐 Diplomatic'}
                      {vehicle.plate_type === 'Military' && '🎖️ Military'}
                      {!['Private', 'Commercial', 'Government', 'Diplomatic', 'Military'].includes(vehicle.plate_type) && vehicle.plate_type}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500 text-xs">Not Set</span>
                  )}
                </td>

                {/* Assigned User */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  {vehicle.user_id && vehicle.user_name ? (
                    <div>
                      <div className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                        {vehicle.user_name}
                      </div>
                      {vehicle.user_email && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          ✉️ {vehicle.user_email}
                        </div>
                      )}
                      {vehicle.user_phone && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          📞 {vehicle.user_phone}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500 text-xs">No User</span>
                  )}
                </td>

                {/* Customer Type */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold shadow-sm ${vehicle.customer_type === 'VIP'
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900'
                    : 'bg-gradient-to-r from-gray-200 to-gray-300 text-gray-800 dark:from-gray-600 dark:to-gray-700 dark:text-gray-200'
                    }`}>
                    {vehicle.customer_type === 'VIP' ? '⭐ VIP' : '👤 Normal'}
                  </span>
                </td>

                {/* Vehicle Type */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  <div className="font-medium text-gray-800 dark:text-gray-200">
                    {vehicle.vehicle_type ? getVehicleTypeLabel(vehicle.vehicle_type) : '-'}
                  </div>
                </td>

                {/* Truck Type */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  {['truck_6_wheels', 'truck_10_wheels', 'truck_10_plus_wheels'].includes(vehicle.vehicle_type) ? (
                    <div>
                      {vehicle.truck_type_id ? (
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-1 rounded bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 text-xs font-bold">
                              {vehicle.truck_type_code || `ID: ${vehicle.truck_type_id}`}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {vehicle.truck_type_name || '-'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-red-500 dark:text-red-400 text-xs font-medium">
                          ⚠️ Not Set
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500 text-xs">N/A</span>
                  )}
                </td>

                {/* Make/Model/Year */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  <div>
                    {vehicle.make && (
                      <div className="font-semibold text-gray-700 dark:text-gray-300 text-xs">
                        {vehicle.make}
                      </div>
                    )}
                    <div className="font-medium text-gray-800 dark:text-gray-200">
                      {vehicle.model || vehicle.vehicle_model || '-'}
                    </div>
                    {vehicle.year && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        📅 {vehicle.year}
                      </div>
                    )}
                  </div>
                </td>

                {/* Color */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  <div className="flex items-center gap-2">
                    {(vehicle.color || vehicle.vehicle_color) && (
                      <>
                        <span
                          className="w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600 shadow-sm"
                          style={{ backgroundColor: getColorHexCode(vehicle.color || vehicle.vehicle_color) }}
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {vehicle.color || vehicle.vehicle_color}
                        </span>
                      </>
                    )}
                    {!vehicle.color && !vehicle.vehicle_color && (
                      <span className="text-gray-400 dark:text-gray-500">-</span>
                    )}
                  </div>
                </td>

                {/* Owner */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  <div>
                    <div className="font-medium text-gray-800 dark:text-gray-200">
                      {vehicle.owner_name}
                    </div>
                    {vehicle.owner_phone && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        📞 {vehicle.owner_phone}
                      </div>
                    )}
                  </div>
                </td>

                {/* Device */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  <div className="text-center">
                    <div>
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium shadow-sm ${vehicle.device_type === 'mifare'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                        }`}>
                        {vehicle.device_type === 'mifare' ? '💳 Mifare' : '📡 OBU-SBU'}
                      </span>
                    </div>
                    <div className="font-mono text-xs text-gray-600 dark:text-gray-400 mt-2">
                      {vehicle.device_type === 'mifare'
                        ? (vehicle.mifare_card_uid || vehicle.device_id || '-')
                        : (vehicle.obu_sbu_id || vehicle.device_id || '-')
                      }
                    </div>
                    {vehicle.device_type === 'mifare' && vehicle.balance && (
                      <div className="text-xs text-green-600 dark:text-green-400 mt-1 font-semibold">
                        {parseFloat(vehicle.balance).toLocaleString()} LAK
                      </div>
                    )}
                  </div>
                </td>

                {/* Status */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  <div className="flex justify-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm ${vehicle.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      vehicle.status === 'inactive' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                        vehicle.status === 'suspended' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          vehicle.status === 'maintenance' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                      {vehicle.status === 'active' && '🟢 Active'}
                      {vehicle.status === 'inactive' && '🔴 Inactive'}
                      {vehicle.status === 'suspended' && '⚠️ Suspended'}
                      {vehicle.status === 'maintenance' && '🔧 Maintenance'}
                      {!vehicle.status && '🟢 Active'}
                    </span>
                  </div>
                </td>

                {/* Actions */}
                <td className="border border-gray-300 dark:border-gray-600 p-3">
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => onEdit(vehicle)}
                      className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      title={t('vehicles.actions.edit_tooltip')}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => onDelete(vehicle.id, vehicle.license_plate)}
                      className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      title={t('vehicles.actions.delete_tooltip')}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
